'use client';

import { useMemo } from 'react';
import { cacheExchange } from '@urql/exchange-graphcache';
import { UrqlProvider, ssrExchange, fetchExchange, createClient } from '@urql/next';
import { keys } from './cacheKeySettings';
import { addressBookCacheUpdates } from './addressBookCacheUpdates';
import { preparedBasketCacheUpdates } from './preparedBasketCacheUpdates';
import { orderConfirmationCacheUpdates } from './orderConfirmationCacheUpdates';

/**
 * The urql client provided by the GqlProvider should only be used for data which is mutable by the user to use the benefits of the graphcache! (e.g. basket or wishlist)
 * When using this client, you can query data with hooks from the `@urql/next` package to enable SSR, streaming and suspense functionality. Keep in mind that the fetched data is streamed from the react client on the server and injected into the graphcache in the browser-client. When browser-client-only behavior is desired, then use the hooks from the `urql` package.
 */
export const GqlProviderWithGraphcache = ({
  children,
  resetKey,
}: React.PropsWithChildren<{ resetKey?: string }>) => {
  const [client, ssr] = useMemo(() => {
    const ssrEx = ssrExchange();
    const urqlClient = createClient({
      url:
        process.env.GQL_GATEWAY ??
        `${typeof window === 'undefined' ? process.env.SERVER_HOST : ''}/api/gateway/`,
      exchanges: [
        cacheExchange({
          keys,
          updates: {
            Mutation: {
              ...addressBookCacheUpdates,
              ...preparedBasketCacheUpdates,
              ...orderConfirmationCacheUpdates,
            },
          },
        }),
        ssrEx,
        fetchExchange,
      ],
      suspense: true,
    });
    return [urqlClient, ssrEx];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resetKey only triggers a new client
  }, [resetKey]);

  return (
    <UrqlProvider client={client} ssr={ssr}>
      {children}
    </UrqlProvider>
  );
};
